import { Download, File, FileText } from "lucide-react";
import { useState } from "react";
import ImageModal from "./ImageModal";

export type TFilePreviewProps = {
  fileId: string;
  fileName: string;
  fileUrl?: string;
  contentType?: string | null;
  fileSize?: number | null;
  variant?: "inline" | "thumbnail" | "card";
};

export default function FilePreview({
  fileId: _fileId,
  fileName,
  fileUrl,
  contentType,
  fileSize,
  variant = "inline",
}: TFilePreviewProps) {
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Detect file type from content type or file extension
  const getFileType = (): "image" | "pdf" | "document" | "other" => {
    if (contentType) {
      if (contentType.startsWith("image/")) return "image";
      if (contentType === "application/pdf") return "pdf";
      if (contentType.includes("document") || contentType.includes("word") || contentType.includes("text")) {
        return "document";
      }
    }

    // Fallback to extension detection
    const extension = fileName.split(".").pop()?.toLowerCase() ?? "";
    if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(extension)) {
      return "image";
    }
    if (extension === "pdf") return "pdf";
    if (["doc", "docx", "txt", "rtf"].includes(extension)) return "document";

    return "other";
  };

  const fileType = getFileType();
  const isImage = fileType === "image";
  const isPDF = fileType === "pdf";
  const isDocument = fileType === "document";

  const formatFileSize = (bytes: number | null | undefined): string => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isSafeUrl = (url: string): boolean => {
    try {
      const { protocol } = new URL(url);
      return protocol === "https:" || protocol === "http:";
    } catch {
      return false;
    }
  };

  const handleOpenFile = () => {
    if (fileUrl && fileUrl !== "#" && isSafeUrl(fileUrl)) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleImageClick = () => {
    if (isImage && fileUrl && fileUrl !== "#" && isSafeUrl(fileUrl)) {
      setShowImageModal(true);
    }
  };

  // Inline variant - compact preview for messages
  if (variant === "inline") {
    if (isImage && fileUrl && fileUrl !== "#" && !imageError) {
      return (
        <>
          <div
            className="mt-2 max-w-xs cursor-pointer overflow-hidden rounded-lg border border-border transition-opacity hover:opacity-90"
            onClick={handleImageClick}
          >
            <img
              src={fileUrl}
              alt={fileName}
              className="h-auto max-h-48 w-full bg-muted object-contain"
              onError={() => setImageError(true)}
            />
            {fileName && <div className="truncate bg-muted px-2 py-1 text-xs text-muted-foreground">{fileName}</div>}
          </div>
          {showImageModal && fileUrl && (
            <ImageModal
              imageUrl={fileUrl}
              imageName={fileName}
              onClose={() => setShowImageModal(false)}
            />
          )}
        </>
      );
    }

    return (
      <div
        className="mt-2 flex max-w-xs cursor-pointer items-center gap-2 rounded-lg border border-border bg-muted p-2 transition-colors hover:bg-muted"
        onClick={handleOpenFile}
      >
        <div className="flex-shrink-0">
          {isPDF && <FileText className="h-5 w-5 text-red-600" />}
          {isDocument && <FileText className="h-5 w-5 text-primary" />}
          {!isPDF && !isDocument && <File className="h-5 w-5 text-muted-foreground" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{fileName}</p>
          {fileSize && <p className="text-xs text-muted-foreground">{formatFileSize(fileSize)}</p>}
        </div>
        {fileUrl && fileUrl !== "#" && <Download className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
      </div>
    );
  }

  // Thumbnail variant - square thumbnail for grid layouts
  if (variant === "thumbnail") {
    if (isImage && fileUrl && fileUrl !== "#" && !imageError) {
      return (
        <>
          <div
            className="group relative cursor-pointer overflow-hidden rounded-lg border border-border bg-card transition-shadow hover:shadow-lg"
            onClick={handleImageClick}
          >
            <div className="aspect-square w-full overflow-hidden bg-muted">
              <img
                src={fileUrl}
                alt={fileName}
                className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                onError={() => setImageError(true)}
              />
            </div>
            {fileName && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                <p
                  className="truncate text-xs text-white"
                  title={fileName}
                >
                  {fileName}
                </p>
              </div>
            )}
          </div>
          {showImageModal && fileUrl && (
            <ImageModal
              imageUrl={fileUrl}
              imageName={fileName}
              onClose={() => setShowImageModal(false)}
            />
          )}
        </>
      );
    }

    return (
      <div
        className="group relative cursor-pointer overflow-hidden rounded-lg border border-border bg-card transition-shadow hover:shadow-lg"
        onClick={handleOpenFile}
      >
        <div className="flex aspect-square w-full flex-col items-center justify-center bg-muted p-4">
          {isPDF && <FileText className="mb-2 h-12 w-12 text-red-600" />}
          {isDocument && <FileText className="mb-2 h-12 w-12 text-primary" />}
          {!isPDF && !isDocument && <File className="mb-2 h-12 w-12 text-muted-foreground" />}
          {fileName && (
            <p
              className="w-full truncate px-2 text-center text-xs text-foreground"
              title={fileName}
            >
              {fileName}
            </p>
          )}
          {fileSize && <p className="mt-1 text-xs text-muted-foreground">{formatFileSize(fileSize)}</p>}
        </div>
      </div>
    );
  }

  // Card variant - larger card preview
  if (variant === "card") {
    if (isImage && fileUrl && fileUrl !== "#" && !imageError) {
      return (
        <>
          <div
            className="cursor-pointer overflow-hidden rounded-lg border border-border transition-shadow hover:shadow-md"
            onClick={handleImageClick}
          >
            <div className="h-64 w-full overflow-hidden bg-muted">
              <img
                src={fileUrl}
                alt={fileName}
                className="h-full w-full object-contain"
                onError={() => setImageError(true)}
              />
            </div>
            {fileName && (
              <div className="border-t border-border bg-card p-3">
                <p
                  className="truncate text-sm font-medium text-foreground"
                  title={fileName}
                >
                  {fileName}
                </p>
                {fileSize && <p className="mt-1 text-xs text-muted-foreground">{formatFileSize(fileSize)}</p>}
              </div>
            )}
          </div>
          {showImageModal && fileUrl && (
            <ImageModal
              imageUrl={fileUrl}
              imageName={fileName}
              onClose={() => setShowImageModal(false)}
            />
          )}
        </>
      );
    }

    return (
      <div
        className="cursor-pointer rounded-lg border border-border bg-card transition-shadow hover:shadow-md"
        onClick={handleOpenFile}
      >
        <div className="flex items-center gap-3 p-4">
          <div className="flex-shrink-0">
            {isPDF && <FileText className="h-10 w-10 text-red-600" />}
            {isDocument && <FileText className="h-10 w-10 text-primary" />}
            {!isPDF && !isDocument && <File className="h-10 w-10 text-muted-foreground" />}
          </div>
          <div className="min-w-0 flex-1">
            <p
              className="truncate text-sm font-medium text-foreground"
              title={fileName}
            >
              {fileName}
            </p>
            {fileSize && <p className="mt-1 text-xs text-muted-foreground">{formatFileSize(fileSize)}</p>}
          </div>
          {fileUrl && fileUrl !== "#" && <Download className="h-5 w-5 flex-shrink-0 text-muted-foreground" />}
        </div>
      </div>
    );
  }

  return null;
}
